import { useQuery } from '@tanstack/react-query';
import { getIpIntrusionSettings, UserKeys } from '../../../../api/users-api';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import DeleteIpIntrusionAddressesDialog from './delete-ip-intrusion-dialog';
import IpIntrusionToolbar from './ip-intrusion-toolbar';
import AddIpIntrusionAddressesDialog from './add-ip-intrusion-dialog';
import ToggleGpanelIpIntrusionDialog from './toggle-gpanel-ip-intrusion-dialog';
import ToggleApiIpIntrusionDialog from './toggle-api-ip-intrusion-dialog';
import dayjs from 'dayjs';
import AvatarChip from '../../../../shared/avatar-chip';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NetworkPing from '@mui/icons-material/NetworkPing';
import SharedTabHeader from '../../../../shared/shared-tab-header';

export default function IpIntrusion() {
  const { userRoles, changeLogRoles, loggedInUser, currentTier } =
    useUserSettings();

  const [openUpdateIpIntrusion, setOpenDeleteIpIntrusionAddresses] =
    useState(false);

  const [openIpAddressToggleDialog, setOpenIpAddressToggleDialog] =
    useState(false);
  const [openApiIpAddressToggleDialog, setOpenApiIpAddressToggleDialog] =
    useState(false);

  const [openAddIpAddress, setOpenIpddress] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const hasEnterprise = currentTier === 'enterprise';

  const {
    data: ipSettings,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.IpIntrusionSettings],
    queryFn: () => getIpIntrusionSettings(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('ip restriction data'),
    },
  });

  const users =
    ipSettings?.users?.map((user) => {
      return {
        type: 'user',
        value: user,
      };
    }) || [];

  const ipAddresses =
    ipSettings?.ipAddresses?.map((ip) => {
      return {
        type: 'ip',
        value: ip,
      };
    }) || [];

  const ipRanges =
    ipSettings?.ipRanges?.map((ipRanges) => {
      return {
        type: 'ipRange',
        value: ipRanges,
      };
    }) || [];

  const rows = [...users, ...ipAddresses, ...ipRanges];

  const columns: GridColDef<{ type: string; value: string }>[] = useMemo(
    () => [
      {
        field: 'value',
        headerName: 'Allow List',
        editable: false,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'type',
        headerName: 'Rule Type',
        editable: false,
        flex: 1,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'User', value: 'user' },
          { label: 'IP Address', value: 'ip' },
          { label: 'IP Range', value: 'ipRange' },
        ],
        valueGetter: (value: string) => {
          return value;
        },
      },
    ],
    []
  );

  const toolbarProps = useMemo(
    () => ({
      userRoles,
      selectedRows,
      setOpenIpAddress: setOpenIpddress,
      setDeleteIpIntrusionAddresses: setOpenDeleteIpIntrusionAddresses,
      model: ipSettings,
      setOpenIpAddressToggleDialog,
      setOpenApiIpAddressToggleDialog,
    }),
    [ipSettings, selectedRows, userRoles]
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="IP Intrusion" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Add, remove, or update users, IP addresses, or IP address ranges that
          are allowed to access gPanel. You will need to enable IP Intrusion for
          the allow list to take effect.
          {hasEnterprise
            ? ` gPanel API IP Intrusion is enabled and disabled separately.`
            : ''}
        </Typography>
        {ipSettings && ipSettings.lastUpdatedTime && ipSettings.updatedBy ? (
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="caption">
              Last updated{' '}
              {dayjs(ipSettings?.lastUpdatedTime).format('MMM D, YYYY, h:mm A')}{' '}
              by
            </Typography>
            <AvatarChip email={ipSettings?.updatedBy || ''} type="User" />
          </Stack>
        ) : null}

        <SharedDataGridClient
          isRowSelectable={(params: GridRowParams) =>
            params.row.value != loggedInUser
          }
          slots={{
            toolbar: useToolbar(IpIntrusionToolbar, toolbarProps),
            ...createGridOverlays('Allow List', <NetworkPing />),
          }}
          getRowId={function (row) {
            return row.type + '||' + row.value;
          }}
          toolbarQuickFilterWidth={400}
          rows={rows}
          columns={columns}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            height: '56vh',
          }}
          loading={isLoading || isFetching}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          checkboxSelection
        />
      </Box>
      {openAddIpAddress ? (
        <AddIpIntrusionAddressesDialog
          open={openAddIpAddress}
          handleClose={() => setOpenIpddress(false)}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {ipSettings && selectedRows && selectedRows.length > 0 ? (
        <DeleteIpIntrusionAddressesDialog
          open={openUpdateIpIntrusion}
          handleClose={() => setOpenDeleteIpIntrusionAddresses(false)}
          selectedRows={selectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openIpAddressToggleDialog && ipSettings ? (
        <ToggleGpanelIpIntrusionDialog
          open={openIpAddressToggleDialog}
          handleClose={() => setOpenIpAddressToggleDialog(false)}
          model={ipSettings}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openApiIpAddressToggleDialog && ipSettings ? (
        <ToggleApiIpIntrusionDialog
          open={openApiIpAddressToggleDialog}
          handleClose={() => setOpenApiIpAddressToggleDialog(false)}
          model={ipSettings}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </Stack>
  );
}
